import React from 'react';
import { Avatar, Card, Col, List, Row, Space, Statistic, Tag, Typography, Spin, Table, Tooltip as AntTooltip } from 'antd';
import { DollarOutlined, WalletOutlined, BankOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useGetInformesVentas } from '../../../hooks/useInformes';

const { Title, Text } = Typography;

const PALETA_COLORES = ['#3b82f6', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#22BABB'];

const SeccionVentas = ({ desde, hasta }) => {
    
    const { data, isLoading, isError } = useGetInformesVentas({ desde, hasta });

    if (isLoading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
                <div style={{ marginTop: '16px', color: '#64748b' }}>Analizando facturación...</div>
            </div>
        );
    }

    if (isError || !data) {
        return <div style={{ textAlign: 'center', marginTop: '50px' }}><Text type="danger">Error al cargar el detalle de ventas.</Text></div>;
    }

    const { kpis, metodos, categorias, topProductos } = data;

    // Asegurarnos de que los valores sean números reales
    const totalBruto = Number(kpis.totalBruto) || 0;
    const totalNeto = Number(kpis.totalNeto) || 0;
    const totalFacturas = Number(kpis.transacciones) || 0;
    
    // Usamos el ticketPromedio que ya viene calculado exacto desde el backend
    const ticketPromedio = Number(kpis.ticketPromedio) || 0;

    const getIconoMetodo = (metodo) => {
        const nombre = (metodo || '').toLowerCase();
        if (nombre.includes('efectivo')) return <WalletOutlined />;
        if (nombre.includes('datafono') || nombre.includes('tarjeta')) return <DollarOutlined />;
        return <BankOutlined />;
    };

    return (
        <div style={{ animation: 'fadeIn 0.5s' }}>
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#f8fafc', borderRadius: '12px' }}>
                        <Statistic 
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Ventas Brutas</Text>} 
                            value={Number(kpis.totalBruto) || 0} prefix="$" 
                            formatter={val => Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })} 
                            valueStyle={{ fontWeight: '700', color: '#94a3b8', fontSize: '18px' }} 
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: '12px' }}>
                        <Statistic 
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Devoluciones</Text>} 
                            value={Number(kpis.devoluciones) || 0} prefix="-$" 
                            formatter={val => Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })} 
                            valueStyle={{ fontWeight: '700', color: '#ef4444', fontSize: '18px' }} 
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#f0fdf4', border: '1px solid #dcfce3', borderRadius: '12px' }}>
                        <Statistic 
                            title={
                                <Space>
                                    <Text type="secondary" style={{ fontSize: '14px', color: '#047857', fontWeight: 'bold' }}>Ventas Netas Reales</Text>
                                    <AntTooltip title="El dinero real que ingresó tras descontar las devoluciones a clientes." color="#0f172a">
                                        <InfoCircleOutlined style={{ color: '#047857' }} />
                                    </AntTooltip>
                                </Space>
                            } 
                            value={Number(kpis.totalNeto) || 0} prefix="$" 
                            formatter={val => Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })} 
                            valueStyle={{ fontWeight: '800', color: '#059669', fontSize: '22px' }} 
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} styles={{ body: { padding: '20px' } }} style={{ background: '#f8fafc', borderRadius: '12px' }}>
                        <Statistic 
                            title={<Text type="secondary" style={{ fontSize: '13px' }}>Ticket Promedio Neto</Text>} 
                            value={ticketPromedio} prefix="$" 
                            formatter={val => Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })} 
                            valueStyle={{ fontWeight: '700', color: '#0f172a', fontSize: '18px' }} 
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                <Col xs={24} md={12}>
                    <Card title={<Space><Title level={5} style={{ margin: 0 }}>Ventas por Método de Pago</Title></Space>} bordered={false} style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', height: '100%' }}>
                        {metodos.length === 0 ? <Text type="secondary">No hay datos registrados.</Text> : (
                            <List
                                dataSource={metodos}
                                renderItem={(item, index) => {
                                    const valorItem = Number(item.valor) || 0;
                                    const porcentaje = totalBruto > 0 ? ((valorItem / totalBruto) * 100).toFixed(1) : 0;
                                    const color = PALETA_COLORES[index % PALETA_COLORES.length];
                                    
                                    return (
                                        <List.Item style={{ border: 'none', padding: '12px 0', display: 'block' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <Space>
                                                    <Avatar style={{ backgroundColor: `${color}20`, color: color }} size="small">
                                                        {getIconoMetodo(item.metodo)}
                                                    </Avatar>
                                                    <Text strong>{item.metodo}</Text>
                                                </Space>
                                                <Text strong>${valorItem.toLocaleString('es-CO', { maximumFractionDigits: 0 })}</Text>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                                                    <div style={{ width: `${porcentaje}%`, background: color, height: '100%', borderRadius: '4px' }}></div>
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px', width: '40px', textAlign: 'right' }}>{porcentaje}%</Text>
                                            </div>
                                        </List.Item>
                                    );
                                }}
                            />
                        )}
                    </Card>
                </Col>

                <Col xs={24} md={12}>
                    <Card title={<Space><Title level={5} style={{ margin: 0 }}>Distribución por Categorías</Title></Space>} bordered={false} style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)', height: '100%' }}>
                        {categorias.length === 0 ? <Text type="secondary">No hay datos registrados.</Text> : (
                            <List
                                dataSource={categorias}
                                renderItem={(item, index) => {
                                    const valorItem = Number(item.valor) || 0;
                                    const porcentaje = totalBruto > 0 ? ((valorItem / totalBruto) * 100).toFixed(1) : 0;
                                    const color = item.color || PALETA_COLORES[(index + 2) % PALETA_COLORES.length];

                                    return (
                                        <List.Item style={{ border: 'none', padding: '12px 0', display: 'block' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                                <Text strong style={{ color: '#475569' }}>{item.categoria}</Text>
                                                <Text strong>${valorItem.toLocaleString('es-CO', { maximumFractionDigits: 0 })}</Text>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                <div style={{ flex: 1, background: '#f1f5f9', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                                                    <div style={{ width: `${porcentaje}%`, background: color, height: '100%', borderRadius: '4px' }}></div>
                                                </div>
                                                <Text type="secondary" style={{ fontSize: '12px', width: '40px', textAlign: 'right' }}>{porcentaje}%</Text>
                                            </div>
                                        </List.Item>
                                    );
                                }}
                            />
                        )}
                    </Card>
                </Col>

                <Col xs={24}>
                    <Card title={<Space><Title level={5} style={{ margin: 0 }}>Desglose Total de Productos Vendidos</Title></Space>} bordered={false} style={{ borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)' }}>
                        <Table
                            dataSource={topProductos}
                            rowKey="id"
                            pagination={{ pageSize: 10 }}
                            columns={[
                                { title: 'Ranking', render: (_, __, index) => <Text strong type="secondary">#{index + 1}</Text>, width: 80, align: 'center' },
                                { title: 'Producto', dataIndex: 'nombre', render: (text) => <Text strong>{text}</Text> },
                                { title: 'Unidades Vendidas', dataIndex: 'ventas', align: 'right', render: (val) => <Tag color="blue">{val} unds</Tag> },
                                { title: 'Ingresos Generados', dataIndex: 'ingresos', align: 'right', render: (val) => <Text strong style={{ color: '#0f172a' }}>${Number(val).toLocaleString('es-CO', { maximumFractionDigits: 0 })}</Text> },
                            ]}
                        />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default SeccionVentas;